/* Print A Bill — base bill for a custom face value.
 *
 * The custom face value is printed onto an existing bill's design, and this
 * module records which design the customer chose.
 *
 * Custom must display that chosen base bill artwork. The retired generic
 * PAB_CUSTOM_DESIGN image and text-only CUSTOM DESIGN placeholder are not valid
 * preview states. This module owns the compatibility-facing chip state, caption,
 * and hidden field that becomes properties[Base Bill].
 *
 * pab-mockup.js and pab-mockup-bridge.js also participate in Custom selection
 * and restoration. This module observes their state and re-renders on the next
 * frame so all three surfaces resolve the same bill design.
 *
 * Ownership, kept deliberately narrow:
 *   - the chosen base denomination, which nothing else tracks
 *   - the preview, only while a custom denomination is selected
 * Everything else — denomination selection, series selection, the Denomination
 * Change fee, pricing — stays where it already lives. Nothing here changes a
 * price: a base bill is a design choice, not a chargeable option.
 */
(function () {
  'use strict';

  var SENTINEL = '$custom';
  var DEFAULT_BASE = '100';

  /* $1 and $2 exist only as 1990 Series artwork. PAB_BILL_PREVIEW_FILES maps
   * them to the same 90s_* files under every series, and
   * resolvePabPreviewImageUrl already forces the label to '1990 Series' for
   * them. Said plainly in the UI so the series choice does not look broken
   * when it stops changing the picture. */
  var SERIES_LOCKED = { '1': true, '2': true };

  var state = { base: DEFAULT_BASE };

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function selectedDenom() {
    var el = $('#denoms .selected');
    return el ? String(el.dataset.denom || '') : '';
  }

  function isCustomSelected() { return selectedDenom() === SENTINEL; }

  function mockupFormatState() {
    if (
      !window.PABMockupState ||
      typeof window.PABMockupState.getState !== 'function'
    ) return null;
    var current = window.PABMockupState.getState();
    return current && typeof current === 'object' ? current : null;
  }

  function selectedSeries() {
    var current = mockupFormatState();
    if (current && current.billSeries) return String(current.billSeries);
    var el = $('#series .selected');
    return el ? String(el.dataset.series || '') : 'New Series';
  }

  function customValue() {
    return window.PABCustomDenomination ? window.PABCustomDenomination.value() : '';
  }

  function buttons() { return $$('#pab-base-bill button[data-base-denom]'); }

  function syncBaseFromMockup() {
    if (!isCustomSelected()) return;
    var current = mockupFormatState();
    if (current && current.baseBill) {
      state.base = String(current.baseBill).replace(/^\$/, '').trim();
      return;
    }
    if (window.PAB && typeof window.PAB.normalizePabBaseBill === 'function') {
      state.base = window.PAB.normalizePabBaseBill(
        selectedSeries(),
        state.base
      ).replace(/^\$/, '');
    }
  }

  function renderSelection() {
    buttons().forEach(function (b) {
      var on = String(b.dataset.baseDenom || '') === state.base;
      b.classList.toggle('selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function renderVisibility() {
    var field = document.getElementById('pab-base-bill-field');
    if (field) field.hidden = !isCustomSelected();
  }

  function renderHelp() {
    var help = document.getElementById('pab-base-bill-help');
    if (!help) return;
    help.textContent = SERIES_LOCKED[state.base]
      ? 'The custom value is printed on this bill\u2019s design. $1 and $2 always print the 1990 Series artwork, whichever series you choose.'
      : 'The custom value is printed on this bill\u2019s design.';
  }

  /* Resolve Custom to the selected base bill artwork.
   *
   * The compatibility cleanup removes any retired custom-sample class left by a
   * stale script, then names the base bill and the figure being printed on it.
   * Callout visibility remains owned by the mockup/bridge callout synchronizer. */
  function renderPreview() {
    if (!isCustomSelected()) return;

    var img = document.getElementById('bill-image');
    var inner = $('#pab-mockup-root .bill-inner');
    var resolve = window.PAB && window.PAB.resolvePabPreviewImageUrl;
    if (!img || !resolve) return;

    var url = resolve(selectedSeries(), SENTINEL, state.base);
    if (!url) return;

    img.src = url;
    img.style.display = '';
    if (inner) {
      inner.classList.remove('custom-sample');
      inner.classList.add('pab-custom-over-base');
    }

    var value = customValue();
    var denomLabel = document.getElementById('bill-denom');
    if (denomLabel) denomLabel.textContent = value || 'YOUR VALUE';

    var seriesLabel = document.getElementById('bill-series');
    if (seriesLabel) {
      var seriesText = SERIES_LOCKED[state.base] ? '1990 SERIES' : selectedSeries().toUpperCase();
      seriesLabel.textContent = seriesText + ' \u00B7 $' + state.base + ' BASE BILL';
    }
  }

  function render() {
    syncBaseFromMockup();
    renderVisibility();
    renderSelection();
    renderHelp();
    renderPreview();
    syncBridge();
  }

  /* The hidden field is the bridge to the cart: pab-mockup-bridge.js copies it
   * into properties[Base Bill]. Written here as well as there so a value is
   * present even if the bridge has not run since the last change. */
  function syncBridge() {
    var field = document.getElementById('billBaseBillField');
    if (field) field.value = isCustomSelected() ? property() : '';
  }

  function property() {
    return isCustomSelected() ? '$' + state.base : '';
  }

  function set(base) {
    var next = String(base == null ? '' : base).replace(/^\$/, '').trim();
    if (!next) return;
    var known = buttons().some(function (b) {
      return String(b.dataset.baseDenom || '') === next;
    });
    if (!known) return;
    state.base = next;
    render();
  }

  var api = {
    get: function () { return state.base; },
    /* The cart property form, '$5'. Blank unless custom is selected, so a
     * standard build never claims a base bill. */
    property: property,
    set: set,
    /* Used by pab-mockup-bridge.js when restoring a saved configuration or a
     * cart line being edited. */
    restore: function (value) {
      var raw = String(value == null ? '' : value).replace(/^\$/, '').trim();
      if (raw) state.base = raw;
      render();
    }
  };

  /* Re-assert the preview if a stale or legacy renderer paints over it.
   *
   * The moment custom-sample reappears, or the image is hidden while Custom is
   * selected, restore the chosen base artwork. `applying` keeps our own
   * mutations from re-entering. */
  var applying = false;

  function enforcePreview() {
    if (applying || !isCustomSelected()) return;
    var inner = $('#pab-mockup-root .bill-inner');
    var img = document.getElementById('bill-image');
    if (!inner || !img) return;
    if (!inner.classList.contains('custom-sample') && img.style.display !== 'none') return;
    applying = true;
    renderPreview();
    window.setTimeout(function () { applying = false; }, 0);
  }

  function watchPreview() {
    var stage = document.getElementById('bill-preview');
    if (!stage || !window.MutationObserver) return;
    new window.MutationObserver(enforcePreview).observe(stage, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'src']
    });
  }

  function init() {
    if (!document.getElementById('pab-mockup-root')) return;

    buttons().forEach(function (b) {
      b.addEventListener('click', function () {
        set(b.dataset.baseDenom);
      });
    });

    /* Denomination and series chips belong to pab-mockup.js. Observe on the
     * next frame so its handler — including setCustomPreview() — has finished
     * before we re-render over the top of it. */
    $$('#denoms button').concat($$('#series button')).forEach(function (b) {
      b.addEventListener('click', function () {
        window.requestAnimationFrame(render);
      });
    });

    /* The typed figure is owned by pab-custom-denomination.js; the caption has
     * to follow it as it changes. */
    var input = document.getElementById('pab-custom-denom-input');
    if (input) {
      input.addEventListener('input', function () {
        window.requestAnimationFrame(renderPreview);
      });
      input.addEventListener('blur', function () {
        window.requestAnimationFrame(renderPreview);
      });
    }

    watchPreview();
    render();
  }

  window.PABBaseBill = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
